import React, { useEffect, useRef, useState } from 'react';
import './TestimonialSlider.scss';
import baseLogo from '../../../assets/images/base-logo.svg';
import viessmannLogo from '../../../assets/images/viessmann-logo.svg';
import worksmileLogo from '../../../assets/images/worksmile-logo.svg';
import baseAvatar from '../../../assets/images/base-testimonial.jpg';
import viessmannAvatar1 from '../../../assets/images/viessmann-testimonial-1.png';
import viessmannAvatar2 from '../../../assets/images/viessmann-testimonial-2.jpg';
import worksmileAvatar from '../../../assets/images/worksmile-testimonial.jpg';

type Testimonial = {
  quote: string;
  name: string;
  role: string;
  brand: string;
  logo: string;
  avatar?: string; // photo; falls back to coloured initials when absent
};

// Marketing testimonials for the signup left panel. Plain content (not i18n by design).
const TESTIMONIALS: Testimonial[] = [
  {
    quote:
      'Chatbeat’s biggest benefit is that we’re no longer guessing. We can actually see the impact of what we’re doing — just like in SEO or paid campaigns, you need proof it’s working.',
    name: 'Karina Kondraciuk',
    role: 'Senior SEO Specialist, Base',
    brand: 'Base',
    logo: baseLogo,
    avatar: baseAvatar,
  },
  {
    quote:
      'The biggest quick win was an immediate map of where we already win in AI — and where AI doesn’t see us, so we can step in as a leader.',
    name: 'Ewelina Bartnikowska',
    role: 'Head of Marketing, Viessmann',
    brand: 'Viessmann',
    logo: viessmannLogo,
    avatar: viessmannAvatar2,
  },
  {
    quote:
      'Chatbeat gives us a quick, simple way to see how we’re positioned in LLMs at any moment — quantitatively. We get fast access to the data and can react quickly.',
    name: 'Magdalena Radecka',
    role: 'Head of Marketing, Worksmile',
    brand: 'Worksmile',
    logo: worksmileLogo,
    avatar: worksmileAvatar,
  },
  {
    quote:
      'Chatbeat lets us remodel key content into a more AI-friendly structure, built around the most common prompts. AI positioning is paying off across our key topics.',
    name: 'Marek Mrozicki',
    role: 'Digital Marketing Manager, Viessmann',
    brand: 'Viessmann',
    logo: viessmannLogo,
    avatar: viessmannAvatar1,
  },
];

const ROTATE_MS = 6000;
const FADE_MS = 400; // one half of the crossfade; keep in sync with the CSS transition

const getInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const TestimonialSlider: React.FC = () => {
  const [active, setActive] = useState(0); // target slide (drives dots + auto-advance)
  const [shown, setShown] = useState(0); // slide currently rendered in the card
  const [visible, setVisible] = useState(true); // false during the fade-out half of a swap
  const paused = useRef(false);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (!paused.current) {
        setActive((prev) => (prev + 1) % TESTIMONIALS.length);
      }
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  // Crossfade: fade the content out, swap it while invisible, then fade back in — so the text
  // never hard-cuts (no "blink"). Must stay in sync with the CSS transition duration below.
  useEffect(() => {
    if (active === shown) return;
    setVisible(false);
    const id = window.setTimeout(() => {
      setShown(active);
      setVisible(true);
    }, FADE_MS);
    return () => window.clearTimeout(id);
  }, [active, shown]);

  const current = TESTIMONIALS[shown];

  return (
    <div
      className="testimonial-slider"
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
    >
      <div className="testimonial-slider__stage">
        <span className="testimonial-slider__peek testimonial-slider__peek--left" />
        <span className="testimonial-slider__peek testimonial-slider__peek--right" />

        <div className="testimonial-slider__card">
          <div
            className={`testimonial-slider__content${
              visible ? '' : ' testimonial-slider__content--hidden'
            }`}
          >
            <p className="testimonial-slider__quote">{`“${current.quote}”`}</p>

            <div className="testimonial-slider__footer">
              <div className="testimonial-slider__author">
                {current.avatar ? (
                  <img
                    className="testimonial-slider__avatar testimonial-slider__avatar--photo"
                    src={current.avatar}
                    alt={current.name}
                  />
                ) : (
                  <span
                    className={`testimonial-slider__avatar testimonial-slider__avatar--${shown + 1}`}
                  >
                    {getInitials(current.name)}
                  </span>
                )}
                <span className="testimonial-slider__meta">
                  <span className="testimonial-slider__name">{current.name}</span>
                  <span className="testimonial-slider__role">{current.role}</span>
                </span>
              </div>
              <img
                className="testimonial-slider__brand"
                src={current.logo}
                alt={current.brand}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="testimonial-slider__dots">
        {TESTIMONIALS.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`testimonial-slider__dot${
              index === active ? ' testimonial-slider__dot--active' : ''
            }`}
            aria-label={`Testimonial ${index + 1}`}
            onClick={() => setActive(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialSlider;
